import * as React from "react";
import { ICompasAIChatAction, ICompassMessage } from "@/types/compass.types";
import { useDocument } from "@/hooks/useDocument";
import { useRunCompassActions, useReorderActions } from "@/hooks/useCompassActions";
import { useCancelQueuedAction } from "@/hooks/useCompassQueue";
import { toast } from "sonner";

export const getActionState = (action?: ICompasAIChatAction): string => {
  if (action?.status) {
    if (action.status === "processing") return "processing";
    if (action.status === "queued") return "queued";
    if (action.status === "pending") return "pending";
    if (action.status === "completed") return "processed";
    if (action.status === "failed") return "failed";
  }
  return "normal";
};

interface ActionItemsHookArgs {
  message: ICompassMessage;
  actions: ICompasAIChatAction[];
  setActions: React.Dispatch<React.SetStateAction<ICompasAIChatAction[]>>;
  setIsBulkActionChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

// External helpers
function actionsAreUnchanged(
  original: ICompasAIChatAction[],
  reordered: ICompasAIChatAction[]
): boolean {
  return original.map((a) => a.id).join(",") === reordered.map((a) => a.id).join(",");
}

function buildReorderPayload(
  reorderedActions: ICompasAIChatAction[],
  messageId: string,
  docId: string
) {
  return {
    action_ids: reorderedActions.map((action) => action.id),
    message_id: messageId,
    context_id: docId,
  };
}

const RUNNABLE_STATES = new Set(["normal", "suggested", "failed", "cancelled"]);

function buildRunActionsPayload(actions: ICompasAIChatAction[], docId: string, docTitle: string) {
  // Only submit actions that are not already queued, processing, or completed
  const runnableActions = actions.filter(
    (action) => action.status == null || RUNNABLE_STATES.has(action.status)
  );
  return {
    requests: runnableActions.map((action) => ({
      id: action.id,
      prompt: action.prompt,
      module: action.module || "",
      feature: action.feature || "",
      current_document_id: docId,
      document_ids: [docId],
      document_title: [docTitle],
    })),
    documentId: docId,
  };
}

function extractJobIdsFromActions(actions: ICompasAIChatAction[]): string[] {
  const cancellableStates = new Set(["queued", "pending"]);
  return actions
    .filter(
      (action): boolean =>
        cancellableStates.has(getActionState(action)) && action.queue_item_id != null
    )
    .map((action) => action.queue_item_id)
    .filter((id): id is string => id != null);
}

// Handler functions extracted outside the hook
function createHandleReorder(
  actions: ICompasAIChatAction[],
  message: ICompassMessage,
  doc: { _id?: string } | undefined,
  setActions: React.Dispatch<React.SetStateAction<ICompasAIChatAction[]>>,
  reorderActionsMutation: {
    mutate: (
      payload: {
        action_ids: string[];
        message_id: string;
        context_id: string;
      },
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      }
    ) => void;
  }
) {
  return (reorderedActions: ICompasAIChatAction[]) => {
    try {
      if (!Array.isArray(reorderedActions)) {
        console.warn("handleReorder received non-array:", reorderedActions);
        return;
      }
      if (actionsAreUnchanged(actions, reorderedActions)) {
        // Order unchanged — no state update or API call needed
        return;
      }
      setActions(reorderedActions);
      if (doc?._id == null || message.id == null) return;
      const payload = buildReorderPayload(reorderedActions, message.id, doc._id);
      // useReorderActions already invalidates ["compass-chat-history"] on success
      reorderActionsMutation.mutate(payload, {
        onError: (error) => {
          console.error("Failed to reorder actions:", error);
          setActions(message.actions ?? []);
        },
      });
    } catch (error) {
      console.error("Error in handleReorder:", error);
    }
  };
}

function createHandleRunAllActions(
  actions: ICompasAIChatAction[],
  doc: { _id?: string; title?: string } | undefined,
  setIsBulkActionChecked: React.Dispatch<React.SetStateAction<boolean>>,
  runActionsMutation: {
    mutate: (
      payload: ReturnType<typeof buildRunActionsPayload>,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      }
    ) => void;
  }
) {
  return () => {
    if (doc?._id == null || doc.title == null || actions.length === 0) {
      console.log("[BulkAction] Early return: doc or actions missing", {
        doc: doc?._id,
        title: doc?.title,
        actionsLen: actions.length,
      });
      return;
    }
    const payload = buildRunActionsPayload(actions, doc._id, doc.title);
    console.log("[BulkAction] Payload:", {
      totalActions: actions.length,
      runnableRequests: payload.requests.length,
      statuses: actions.map((a) => a.status),
    });
    if (payload.requests.length === 0) {
      console.log("[BulkAction] No runnable actions, skipping");
      return;
    }
    // useRunCompassActions already invalidates ["compass-chat-history"] on success
    runActionsMutation.mutate(payload, {
      onSuccess: () => {
        toast.success("Compass started processing bulk actions", { duration: 2000 });
        setIsBulkActionChecked(false);
      },
      onError: (error) => {
        toast.error("Failed to execute bulk actions", { duration: 2000 });
        console.error("Failed to execute bulk actions:", error);
        setIsBulkActionChecked(false);
      },
    });
  };
}

function createHandleCancelAllPendingActions(
  actions: ICompasAIChatAction[],
  doc: { _id?: string } | undefined,
  setIsBulkActionChecked: React.Dispatch<React.SetStateAction<boolean>>,
  cancelActionsMutation: {
    mutate: (
      payload: { jobIds: string[]; documentId: string },
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      }
    ) => void;
  }
) {
  return () => {
    if (doc?._id == null) {
      setIsBulkActionChecked(false);
      return;
    }
    const jobIds = extractJobIdsFromActions(actions);
    if (jobIds.length === 0) {
      setIsBulkActionChecked(false);
      return;
    }
    // useCancelQueuedAction already invalidates both queued-actions and compass-chat-history on success
    cancelActionsMutation.mutate(
      { jobIds: jobIds, documentId: doc._id },
      {
        onError: (error) => {
          console.error("Failed to cancel bulk actions:", error);
          setIsBulkActionChecked(true);
        },
      }
    );
    setIsBulkActionChecked(false);
  };
}

function createMoveAction(
  actions: ICompasAIChatAction[],
  handleReorder: (reordered: ICompasAIChatAction[]) => void
) {
  return (fromIndex: number, toIndex: number) => {
    const reorderedActions = [...actions];
    const [item] = reorderedActions.splice(fromIndex, 1);
    if (!item) return;
    reorderedActions.splice(toIndex, 0, item);
    handleReorder(reorderedActions);
  };
}

export const useActionItemsHandlers = ({
  message,
  actions,
  setActions,
  setIsBulkActionChecked,
}: ActionItemsHookArgs) => {
  const { data: doc } = useDocument();
  const runActionsMutation = useRunCompassActions();
  const cancelActionsMutation = useCancelQueuedAction();
  const reorderActionsMutation = useReorderActions();

  const handleReorder = createHandleReorder(
    actions,
    message,
    doc,
    setActions,
    reorderActionsMutation
  );

  const handleRunAllActions = createHandleRunAllActions(
    actions,
    doc,
    setIsBulkActionChecked,
    runActionsMutation
  );

  const handleCancelAllPendingActions = createHandleCancelAllPendingActions(
    actions,
    doc,
    setIsBulkActionChecked,
    cancelActionsMutation
  );

  const moveAction = createMoveAction(actions, handleReorder);

  return { doc, handleReorder, handleRunAllActions, handleCancelAllPendingActions, moveAction };
};
